// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CheckboxInput.tsx
 * @input Uses React, useId, ChangeEvent, FieldLabel, FieldStatus, IconType, InputStatus, useTooltip
 * @output Exports CheckboxInput component, CheckboxInputProps
 * @position Core implementation; consumed by index.ts, tested by CheckboxInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/CheckboxInput/CheckboxInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/CheckboxInput/CheckboxInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/CheckboxInput/index.ts (exports if types change)
 * - /apps/storybook/stories/CheckboxInput.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/CheckboxInput/ (showcase blocks)
 */
import { useId, useCallback, use, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, typeScaleVars, fontWeightVars, borderVars } from "../theme/tokens.stylex.js";
import { FieldLabel } from "../Field/FieldLabel.js";
import { FieldStatus } from "../FieldStatus/FieldStatus.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import "./checkbox.markers.stylex.js";
import { checkboxScope } from "./checkbox.markers.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { CheckboxListContext } from "../CheckboxList/CheckboxListContext.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  containerLabelHidden: {
    kOIVth: "xxhr3t",
    $$css: true
  },
  checkboxWrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kHBbk8: "xc8icb0",
    $$css: true
  },
  input: {
    kVAEAm: "x10l6tqk",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kkrTdU: "x1ypdohk",
    kY2c9j: "x1vjfegm",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  checkbox: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kaIpWk: "xx3sua9",
    k1ekBW: "xts7igz",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  checkboxFocus: {
    kI3sdo: "x1a2a7pz xkri23q",
    kInvED: "x81n7kt",
    $$css: true
  },
  checkboxUnchecked: {
    kMwMTN: "xqwr325",
    kVAM5u: "xvy26l8 x1jtjlen",
    kWkggS: "x10xzikg xrsv7h",
    $$css: true
  },
  checkboxChecked: {
    kMwMTN: "x17wrial",
    kVAM5u: "xad5do x1od6ugu",
    kWkggS: "x1ewilqj x6aeo4j",
    $$css: true
  },
  checkboxDisabled: {
    kSiTet: "xbyyjgo",
    kVAM5u: "x14i3s5s xmt8yqp",
    $$css: true
  },
  checkboxDisabledUnchecked: {
    kWkggS: "xwmxj5m x1cjfj8k",
    $$css: true
  },
  checkmark: {
    k1xSpc: "x1s85apg",
    kMwMTN: "x17wrial",
    $$css: true
  },
  checkmarkVisible: {
    k1xSpc: "x1lliihq",
    $$css: true
  },
  indeterminateMark: {
    k1xSpc: "x1s85apg",
    kWkggS: "x1azo05",
    kaIpWk: "x1nj7uno",
    $$css: true
  },
  indeterminateMarkVisible: {
    k1xSpc: "x1lliihq",
    $$css: true
  }
};
const wrapperSizeStyles = {
  sm: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const checkboxSizeStyles = {
  sm: {
    kzqmXN: "x1xp8n7a",
    kZKoxP: "xmix8c7",
    $$css: true
  },
  md: {
    kzqmXN: "x17z2i9w",
    kZKoxP: "x17rw0jw",
    $$css: true
  }
};
const checkmarkSizeStyles = {
  sm: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x1kpxq89",
    $$css: true
  },
  md: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    $$css: true
  }
};
const indeterminateSizeStyles = {
  sm: {
    kzqmXN: "x1fsd2vl",
    kZKoxP: "x36qwtl",
    $$css: true
  },
  md: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x36qwtl",
    $$css: true
  }
};
// Dynamic field width (number -> px, string used as-is).
const dynamicWidthStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};

/**
 * A checkbox input component for toggling boolean values.
 *
 * @example
 * ```
 * <CheckboxInput
 *   label="Accept terms"
 *   value={accepted}
 *   onChange={setAccepted}
 * />
 * <CheckboxInput
 *   label="Subscribe"
 *   description="Receive weekly updates"
 *   value={subscribed}
 *   onChange={setSubscribed}
 * />
 * ```
 */
export function CheckboxInput({
  label,
  isLabelHidden = false,
  description,
  onChange,
  changeAction,
  isLoading = false,
  value,
  isDisabled = false,
  htmlName,
  disabledMessage,
  isReadOnly = false,
  isOptional = false,
  isRequired = false,
  size = 'md',
  onFocus,
  onBlur,
  labelIcon,
  status,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the checkbox row (which already exists) and the
  // native checkbox stays perceivable via aria-disabled instead of the disabled
  // attribute. Value mutation is blocked by the isDisabled guard in onChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  // Keep the native checkbox focusable via aria-disabled either when it renders
  // its own reason tooltip, or when it sits in a CheckboxList whose whole-group
  // `disabledMessage` (shown on the group container) needs each checkbox to
  // stay keyboard-perceivable. The group signals this through context rather
  // than a public prop.
  const checkboxListContext = use(CheckboxListContext);
  const isFocusableDisabled = isDisabled && (showsDisabledMessage || (checkboxListContext?.hasDisabledMessage ?? false));
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container row is not naturally focusable; focusin bubbles up from the
    // native checkbox, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const isIndeterminate = optimisticValue === 'indeterminate';
  const isChecked = optimisticValue === true;
  const isCheckedOrIndeterminate = isChecked || isIndeterminate;

  // Sync the native indeterminate DOM property (can't be set via JSX
  // attribute). On a native checkbox this is the authoritative way to expose
  // the mixed state — a separate aria-checked="mixed" would be redundant and
  // can desync from / override the native state (forms-16), so it is omitted.
  const indeterminateRef = useCallback(el => {
    if (el) {
      el.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Build aria-describedby from description and status message
  // Only include descriptionID when the element actually renders.
  // FieldLabel renders the description (with descriptionID) even when the
  // label is visually hidden — it's sr-only, so keep it linked.
  const describedByParts = [];
  if (description) {
    describedByParts.push(descriptionID);
  }
  if (status?.message) {
    describedByParts.push(statusMessageID);
  }
  if (showsDisabledMessage) {
    describedByParts.push(disabledMessageTooltip.describedBy);
  }
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
  return /*#__PURE__*/_jsxs("div", {
    ...mergeProps(themeProps('checkbox-input', {
      size
    }), stylex.props(width != null && dynamicWidthStyles.width(width), xstyle), className, style),
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        // Interaction (hover/focus) listeners for the disabled-message
        // tooltip attach to the whole row for a larger trigger target;
        // positioning anchors on the checkbox itself (below) so the tooltip
        // appears next to the control, not the far edge of the row.
        // Handlers are gated internally by isEnabled, so attaching
        // unconditionally is safe.
        disabledMessageTooltip.interactionRef(el);
      },
      ...stylex.props(styles.container, isLabelHidden && styles.containerLabelHidden, !isDisabled && checkboxScope),
      children: [/*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.checkboxWrapper, wrapperSizeStyles[size]),
        children: [/*#__PURE__*/_jsx("input", {
          ...rest,
          ref: mergeRefs(ref, indeterminateRef, disabledMessageTooltip.positionRef),
          id: id,
          type: "checkbox"
          // Withhold the name while disabled: with a disabledMessage the
          // input stays focusable (not natively disabled), and a disabled
          // control must not submit.
          ,
          name: isDisabled ? undefined : htmlName,
          checked: isChecked
          // With a disabledMessage the checkbox keeps focusability via
          // aria-disabled so the reason is focus-discoverable; toggling is
          // still blocked by the isDisabled guard in onChange below.
          ,
          disabled: isDisabled && !isFocusableDisabled,
          "aria-disabled": isFocusableDisabled ? 'true' : undefined,
          readOnly: isReadOnly,
          required: isRequired,
          onChange: e => {
            if (isDisabled || isBusy || isReadOnly) {
              return;
            }
            const checked = e.target.checked;
            onChange?.(checked, e);
            if (changeAction && !e.defaultPrevented) {
              startTransition(async () => {
                setOptimisticValue(checked);
                await changeAction(checked, e);
              });
            }
          },
          onFocus: onFocus,
          onBlur: onBlur,
          "aria-readonly": isReadOnly || undefined,
          "aria-describedby": ariaDescribedBy,
          "aria-invalid": status?.type === 'error' ? true : undefined,
          "aria-busy": isBusy || undefined,
          ...stylex.props(styles.input, wrapperSizeStyles[size], isDisabled && styles.inputDisabled)
        }), /*#__PURE__*/_jsx("div", {
          "aria-hidden": "true",
          ...mergeProps(themeProps('checkbox', {
            size,
            checked: isChecked ? 'checked' : isIndeterminate ? 'indeterminate' : null,
            disabled: isDisabled ? 'disabled' : null
          }), stylex.props(styles.checkbox, checkboxSizeStyles[size], !isDisabled && styles.checkboxFocus, isCheckedOrIndeterminate ? styles.checkboxChecked : styles.checkboxUnchecked, isDisabled && styles.checkboxDisabled, isDisabled && !isCheckedOrIndeterminate && styles.checkboxDisabledUnchecked)),
          children: isBusy ? /*#__PURE__*/_jsx(Spinner, {
            size: "sm",
            shade: "inherit"
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("svg", {
              viewBox: "0 0 10 10",
              ...stylex.props(styles.checkmark, checkmarkSizeStyles[size], isChecked && styles.checkmarkVisible),
              children: /*#__PURE__*/_jsx("path", {
                d: "M8.5 2.5L4 7.5L1.5 5",
                stroke: "currentColor",
                strokeWidth: "1.5",
                fill: "none",
                strokeLinecap: "round",
                strokeLinejoin: "round"
              })
            }), /*#__PURE__*/_jsx("div", {
              ...stylex.props(styles.indeterminateMark, indeterminateSizeStyles[size], isIndeterminate && styles.indeterminateMarkVisible)
            })]
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        ...{
          className: "x78zum5 xdt5ytf x1lsbc85"
        },
        children: /*#__PURE__*/_jsx(FieldLabel, {
          label: label,
          inputID: id,
          isLabelHidden: isLabelHidden,
          isDisabled: isDisabled,
          isOptional: isOptional,
          isRequired: isRequired,
          labelIcon: labelIcon,
          description: description,
          descriptionID: descriptionID
        })
      })]
    }), status?.message && /*#__PURE__*/_jsx(FieldStatus, {
      type: status.type,
      message: status.message,
      id: statusMessageID,
      variant: "detached"
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
CheckboxInput.displayName = 'CheckboxInput';